import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Printer, Mail, Link as LinkIcon, QrCode, Share2, ChevronRight, ArrowLeft, ShieldAlert } from 'lucide-react';
export function CrisisResponsePage() {
  const [checklist, setChecklist] = useState([false, false, false, false, false]);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [reflectionText, setReflectionText] = useState('');
  const [progress, setProgress] = useState(0);
  const [scenarioAnswer, setScenarioAnswer] = useState<string | null>(null);
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const handleCheck = (index: number) => {
    const newChecklist = [...checklist];
    newChecklist[index] = !newChecklist[index];
    setChecklist(newChecklist);
    setHasInteracted(true);
    // Update progress based on checklist and scenario (up to 50% before completion)
    calculateProgress(newChecklist, scenarioAnswer);
  };
  const handleScenario = (answer: string) => {
    setScenarioAnswer(answer);
    setHasInteracted(true);
    calculateProgress(checklist, answer);
  };
  const calculateProgress = (currentChecklist: boolean[], currentScenario: string | null) => {
    if (isCompleted) return;
    let score = 0;
    const totalItems = currentChecklist.length + 1; // checklist items + scenario
    const completedChecks = currentChecklist.filter(Boolean).length;
    score += completedChecks;
    if (currentScenario !== null) {
      score += 1;
    }
    setProgress(Math.round(score / totalItems * 50));
  };
  const handleComplete = () => {
    setIsCompleted(true);
    setProgress(100);
    // Small delay to allow render before scrolling
    setTimeout(() => {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth'
      });
    }, 100);
  };
  return <div className="min-h-screen w-full bg-white text-[#1a0647]" data-id="element-1384" data-mp="13vrk1kh49s3e">
      {/* Simple Navigation Bar */}
      <header className="sticky top-0 z-50 w-full bg-white border-b border-gray-200 shadow-sm" data-id="element-1385" data-mp="1tjwy0djlx0f2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-1386" data-mp="1xli5fjddgmhv">
          <Link to="/resources" className="text-gray-600 hover:text-[#8528e2] font-manrope font-semibold text-base transition-colors flex items-center gap-2 min-h-[44px] min-w-[44px]" data-id="element-1387" data-mp="22gnqyh2v3z5f">
            <ArrowLeft size={20} aria-hidden="true" data-id="element-1388" data-mp="1eogz9v830gcc" />
            Back to Resources
          </Link>
          <img src="https://cdn.magicpatterns.com/uploads/8EVxVy8dkAVEdmFcPkswyr/Caveo_2026_Logo.png" alt="Caveo Logo" className="h-6 w-auto" data-id="element-1389" data-mp="1jo3do7znc2mq" />
        </div>
      </header>

      <main data-id="element-1390" data-mp="2v3s1f1kq55ol">
        {/* 1. Module Header (Hero Block) */}
        <section className="bg-[#1a0647] text-white py-12 md:py-16 px-4 md:px-8" data-id="element-1391" data-mp="2v5cuemre4gkc">
          <div className="max-w-3xl mx-auto" data-id="element-1392" data-mp="3008qpz3ntq39">
            <h1 className="font-manrope text-3xl md:text-5xl font-bold mb-4 text-white leading-tight" data-id="element-1393" data-mp="of9hfkrv8mj4">
              Crisis Response Guide
            </h1>
            <p className="font-inter text-lg md:text-xl mb-8 text-gray-200 leading-relaxed" data-id="element-1394" data-mp="jhq5irin4luc">
              After this module, you'll know exactly what to do when a digital
              crisis hits—whether it's a hacked account, a scam, or identity
              theft.
            </p>

            <div className="inline-flex items-center gap-2 bg-[#01ced1] text-[#1a0647] px-4 py-2 rounded-full font-manrope font-bold text-base mb-10 min-h-[44px]" data-id="element-1395" data-mp="5pjq57nbaivy">
              <span aria-hidden="true" data-id="element-1396" data-mp="3runeqrf8hwaa">⏱️</span> 15 minutes to complete
            </div>

            <div className="space-y-3 bg-white/5 p-6 rounded-xl border border-white/10" data-id="element-1397" data-mp="3mwvjbnfdrs1c">
              <div className="flex justify-between font-manrope font-semibold text-base" data-id="element-1398" data-mp="1q3trrj2x2cs9">
                <span id="progress-label" data-id="element-1399" data-mp="1l4z8yh4y8o85">Your progress</span>
                <span data-id="element-1400" data-mp="1l5s92z9ekfhb">{progress}%</span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-4 overflow-hidden" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} aria-labelledby="progress-label" data-id="element-1401" data-mp="1t5f1yzshl2wr">
                <div className="bg-[#214ae9] h-full transition-all duration-700 ease-out" style={{
                width: `${progress}%`
              }} data-id="element-1402" data-mp="1o5kdf2w6opev" />
              </div>
            </div>
          </div>
        </section>

        {/* 2. The Hook (Story Block) */}
        <section className="bg-[#eae6dc] py-12 md:py-16 px-4 md:px-8" data-id="element-1403" data-mp="10iuck4vkn0vu">
          <div className="max-w-3xl mx-auto flex flex-col sm:flex-row gap-6 items-start" data-id="element-1404" data-mp="15h5l8ca4r5ch">
            <div className="flex-shrink-0 w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm text-3xl border border-gray-200" aria-hidden="true" data-id="element-1405" data-mp="rg30c2uvg135">
              🚨
            </div>
            <div className="flex-1 space-y-4" data-id="element-1406" data-mp="cvqg26eztk64">
              <p className="font-inter text-gray-700 text-lg leading-relaxed" data-id="element-1407" data-mp="7vb51li6oorb">
                Robert was making dinner when his phone buzzed with an urgent
                text from his "bank" about a fraudulent $800 charge. Panicking,
                he clicked the link and entered his login details to stop the
                transfer. A minute later, he realized the website URL looked
                slightly wrong. His heart sank. He had just handed his actual
                bank password to a scammer.
              </p>
              <p className="font-inter text-[#1a0647] text-xl font-bold leading-relaxed" data-id="element-1408" data-mp="14kaev4yf1ylm">
                In this module, you'll learn the exact steps to take to minimize
                damage, secure your accounts, and recover your identity when the
                worst happens.
              </p>
            </div>
          </div>
        </section>

        {/* 3. Concept Cards (One Idea Per Card) */}
        <section className="py-16 md:py-24 px-4 md:px-8 bg-white" data-id="element-1409" data-mp="78bmw8oqdwr1">
          <div className="max-w-4xl mx-auto" data-id="element-1410" data-mp="b93vr0toxzms">
            <h2 className="sr-only" data-id="element-1411" data-mp="b5zgn8eotjhc">Key Concepts</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1412" data-mp="g6427cugd4ef">
              {/* Card 1 */}
              <div className="bg-[#f8f8f8] p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-1413" data-mp="kyx6jzm0eapz">
                <h3 className="font-manrope text-[#1a0647] text-2xl font-bold mb-4" data-id="element-1414" data-mp="pwoyuuqkfyup">
                  Hacked Accounts
                </h3>
                <p className="font-inter text-gray-700 text-base mb-6 leading-relaxed" data-id="element-1415" data-mp="yxwjwb42n36u">
                  If you're locked out of an account or see activity you didn't
                  authorize, immediately use the "Forgot Password" tool to
                  regain access. Once in, change your password, turn on
                  two-factor authentication (2FA), and check your recovery email
                  and phone number to ensure the hacker didn't change them.
                </p>
                <p className="font-inter text-[#8528e2] text-base italic font-medium" data-id="element-1416" data-mp="2tdvanu89tv84">
                  Think of this like changing the locks on your house
                  immediately after losing your keys.
                </p>
              </div>

              {/* Card 2 */}
              <div className="bg-[#f8f8f8] p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-1417" data-mp="2jdmbq8mw9vp1">
                <h3 className="font-manrope text-[#1a0647] text-2xl font-bold mb-4" data-id="element-1418" data-mp="3c4mgwmwyp5ia">
                  Financial Scams
                </h3>
                <p className="font-inter text-gray-700 text-base mb-6 leading-relaxed" data-id="element-1419" data-mp="373yt0a4154sa">
                  If you see unauthorized charges or realize you gave your
                  financial info to a scammer, call your bank or credit card
                  company immediately using the number on the back of your card.
                  They can freeze your account, issue a new card, and start the
                  dispute process to get your money back.
                </p>
                <p className="font-inter text-[#8528e2] text-base italic font-medium" data-id="element-1420" data-mp="3vsmac2lwcnky">
                  Think of this like hitting the emergency stop button on a
                  runaway train.
                </p>
              </div>

              {/* Card 3 */}
              <div className="bg-[#f8f8f8] p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-1421" data-mp="ebuon25e4yjq">
                <h3 className="font-manrope text-[#1a0647] text-2xl font-bold mb-4" data-id="element-1422" data-mp="idprib2yzjzm">
                  Identity Theft
                </h3>
                <p className="font-inter text-gray-700 text-base mb-6 leading-relaxed" data-id="element-1423" data-mp="4lt2wh0ifs7z">
                  If your Social Security number or sensitive data is
                  compromised, immediately place a fraud alert or credit freeze
                  with the three major credit bureaus (Equifax, Experian,
                  TransUnion). Then, file a report at IdentityTheft.gov to get a
                  personalized recovery plan.
                </p>
                <p className="font-inter text-[#8528e2] text-base italic font-medium" data-id="element-1424" data-mp="17fgoatbv2z6j">
                  Think of a credit freeze like putting your financial
                  reputation in a vault that only you can open.
                </p>
              </div>

              {/* Card 4 */}
              <div className="bg-[#f8f8f8] p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-1425" data-mp="sgv2107cvla7">
                <h3 className="font-manrope text-[#1a0647] text-2xl font-bold mb-4" data-id="element-1426" data-mp="oh4d721e3f8b">
                  Tech Support Scams
                </h3>
                <p className="font-inter text-gray-700 text-base mb-6 leading-relaxed" data-id="element-1427" data-mp="xgheh7fd0xhy">
                  If you get a terrifying pop-up saying your computer has a
                  virus and you must call a number, do not call it. Real tech
                  companies do not operate this way. Force quit your browser or
                  restart your computer, then run a scan with your actual
                  antivirus software.
                </p>
                <p className="font-inter text-[#8528e2] text-base italic font-medium" data-id="element-1428" data-mp="2eyy3behdyw2o">
                  Think of this like a stranger banging on your window shouting
                  that your house is on fire, while offering to sell you a
                  bucket of water.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 4. Practice Activity (Interactive Block) */}
        <section className="py-16 md:py-24 px-4 md:px-8 bg-white border-t border-gray-100" data-id="element-1429" data-mp="1w5ub7xjtgra6">
          <div className="max-w-3xl mx-auto" data-id="element-1430" data-mp="1r8awkwj7ichp">
            <h2 className="font-manrope text-[#1a0647] text-3xl md:text-4xl font-bold mb-6" data-id="element-1431" data-mp="1m9hgqxgg69ed">
              Try It Now
            </h2>
            <p className="font-inter text-gray-700 text-lg mb-10 leading-relaxed" data-id="element-1432" data-mp="3lkserpp6umfh">
              The best time to prepare for a crisis is before it happens. Tap
              each step as you complete it to build your emergency response kit.
            </p>

            <div className="space-y-4 mb-12" role="group" aria-label="Preparation Checklist" data-id="element-1433" data-mp="3t5cqdebhrktj">
              {['Save the customer service numbers for your bank and credit cards in your phone contacts.', 'Bookmark IdentityTheft.gov in your web browser.', 'Verify that the recovery email and phone number on your primary email account are up to date.', 'Write down the phone numbers for the three credit bureaus (Equifax: 800-685-1111, Experian: 888-397-3742, TransUnion: 888-909-8872) and keep them with your important documents.', 'Check your credit report for free at AnnualCreditReport.com to ensure there are no existing unauthorized accounts.'].map((item, i) => <button key={i} onClick={() => handleCheck(i)} className={`w-full text-left p-6 rounded-xl border-2 transition-all duration-200 flex items-start gap-5 min-h-[44px] ${checklist[i] ? 'border-[#01ced1] bg-[#01ced1]/5 shadow-sm' : 'border-gray-200 bg-[#f8f8f8] hover:border-[#8528e2]/40 hover:bg-white'}`} aria-pressed={checklist[i]} data-id="element-1434" data-mp="zdmfg7rajj0r" data-mpi={i}>
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center border-2 mt-0.5 transition-colors ${checklist[i] ? 'bg-[#01ced1] border-[#01ced1] text-white' : 'border-gray-300 text-transparent bg-white'}`} data-id="element-1435" data-mp="eqejnr9t94ql">
                    <CheckCircle2 size={20} aria-hidden="true" data-id="element-1436" data-mp="rer48j6g5p5" />
                  </div>
                  <span className={`font-inter text-lg leading-relaxed ${checklist[i] ? 'text-[#1a0647] font-semibold' : 'text-gray-700'}`} data-id="element-1437" data-mp="3s3rboy9ggevw">
                    {item}
                  </span>
                </button>)}
            </div>

            {/* Scenario Choice */}
            <div className="bg-[#f8f8f8] p-8 rounded-2xl border border-gray-200" data-id="element-1438" data-mp="1evqawmn4d4tc">
              <div className="flex items-center gap-3 mb-6" data-id="element-1439" data-mp="1jq4al18tdy3t">
                <ShieldAlert size={28} className="text-[#8528e2]" aria-hidden="true" data-id="element-1440" data-mp="1505dl1szwspx" />
                <h3 className="font-manrope text-[#1a0647] text-2xl font-bold" data-id="element-1441" data-mp="19wnji502ax3u">
                  Quick Check
                </h3>
              </div>
              <p className="font-inter text-gray-700 text-lg mb-6 leading-relaxed" data-id="element-1442" data-mp="22nnke733jkra">
                You notice a $500 charge on your credit card statement that you
                definitely didn't make. What is your very first step?
              </p>

              <div className="space-y-3" data-id="element-1443" data-mp="1sr2gxq6q6hpk">
                <button onClick={() => handleScenario('A')} className={`w-full text-left p-4 rounded-xl border-2 transition-colors min-h-[44px] font-inter text-base ${scenarioAnswer === 'A' ? 'border-[#214ae9] bg-[#214ae9]/5 text-[#1a0647]' : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'}`} data-id="element-1444" data-mp="d87ovui3hxjv">
                  A: Wait a few days to see if the charge disappears or corrects
                  itself.
                </button>
                <button onClick={() => handleScenario('B')} className={`w-full text-left p-4 rounded-xl border-2 transition-colors min-h-[44px] font-inter text-base ${scenarioAnswer === 'B' ? 'border-[#01ced1] bg-[#01ced1]/5 text-[#1a0647]' : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'}`} data-id="element-1445" data-mp="it2owww159p5">
                  B: Call the number on the back of your credit card immediately
                  to report the fraud and freeze the card.
                </button>
                <button onClick={() => handleScenario('C')} className={`w-full text-left p-4 rounded-xl border-2 transition-colors min-h-[44px] font-inter text-base ${scenarioAnswer === 'C' ? 'border-[#214ae9] bg-[#214ae9]/5 text-[#1a0647]' : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'}`} data-id="element-1446" data-mp="3r9o1ennvekx0">
                  C: Try to look up the merchant online and call them to ask for
                  a refund.
                </button>
              </div>

              {scenarioAnswer === 'B' && <div className="mt-6 p-4 bg-[#01ced1]/10 border-l-4 border-[#01ced1] rounded-r-lg animate-in fade-in" data-id="element-1447" data-mp="2lfhtjdz3m442">
                  <p className="font-inter text-[#1a0647] text-base font-medium" data-id="element-1448" data-mp="2qd04tb0l8vf8">
                    That's exactly right! Your credit card company is your best
                    advocate here. They can instantly freeze the card to prevent
                    further charges, issue you a new card, and start the dispute
                    process to remove the fraudulent charge.
                  </p>
                </div>}
              {scenarioAnswer === 'A' && <div className="mt-6 p-4 bg-[#214ae9]/10 border-l-4 border-[#214ae9] rounded-r-lg animate-in fade-in" data-id="element-1449" data-mp="28audumiynfjg">
                  <p className="font-inter text-[#1a0647] text-base font-medium" data-id="element-1450" data-mp="23dugd006fl3y">
                    Not quite — time is critical when dealing with fraud. If
                    someone has your card information, they will likely make
                    more charges. Call the number on the back of your card
                    immediately to freeze it and dispute the charge.
                  </p>
                </div>}
              {scenarioAnswer === 'C' && <div className="mt-6 p-4 bg-[#214ae9]/10 border-l-4 border-[#214ae9] rounded-r-lg animate-in fade-in" data-id="element-1451" data-mp="3031wfsi1120h">
                  <p className="font-inter text-[#1a0647] text-base font-medium" data-id="element-1452" data-mp="2w1qca2hx98zo">
                    Actually, contacting the merchant is usually a dead end, and
                    if it's a scammer, you're just giving them more of your
                    time. Your credit card company has the power to reverse the
                    charge and issue you a new, secure card. Call the number on
                    the back of your card immediately.
                  </p>
                </div>}
            </div>
          </div>
        </section>

        {/* 5. Honesty Gate (Pre-Commitment Block) */}
        <section className="py-8 px-4 md:px-8 bg-white" data-id="element-1453" data-mp="3jmr01zpki7vc">
          <div className="max-w-3xl mx-auto" data-id="element-1454" data-mp="35wx5r1vamve8">
            <div className="border-l-4 border-[#8528e2] bg-[#8528e2]/5 p-6 md:p-8 rounded-r-xl mb-8" data-id="element-1455" data-mp="35v41tuc2ep9x">
              <p className="font-inter text-[#1a0647] text-lg leading-relaxed font-medium" data-id="element-1456" data-mp="3aq019fjmsex2">
                Before you confirm: this step matters for your family's
                protection. Please only mark it complete if you actually did it.
                It's completely okay to come back to this later.
              </p>
            </div>

            {hasInteracted && !isCompleted && <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-1457" data-mp="2r20qi4p2h7rn">
                <button onClick={handleComplete} className="bg-[#8528e2] text-white font-manrope font-bold text-lg px-8 py-4 rounded-xl hover:bg-[#b063ff] transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center w-full md:w-auto shadow-md" data-id="element-1458" data-mp="37enrfyfp5mrl">
                  I completed this step
                </button>
              </div>}
          </div>
        </section>

        {/* 6. Reflection Prompt (Personal Connection Block) */}
        <section className="py-16 md:py-24 px-4 md:px-8 bg-white" data-id="element-1459" data-mp="2jnrukmwdw1dh">
          <div className="max-w-3xl mx-auto" data-id="element-1460" data-mp="2erjkhnx1myd6">
            <div className="bg-[#f8f8f8] border border-gray-200 p-8 md:p-10 rounded-2xl shadow-sm" data-id="element-1461" data-mp="29rosti5g24r2">
              <div className="inline-block bg-[#eae6dc] text-[#1a0647] font-manrope font-bold px-4 py-1.5 rounded-md text-sm mb-6 uppercase tracking-wider" data-id="element-1462" data-mp="24skigo6sa0z5">
                Reflect for a Moment
              </div>
              <h3 className="font-manrope text-[#1a0647] text-2xl md:text-3xl font-bold mb-8 leading-tight" data-id="element-1463" data-mp="g97wk4s5xgqq">
                Which of these crisis scenarios worries you the most, and what's
                one step you can take today to be better prepared?
              </h3>
              <textarea value={reflectionText} onChange={e => setReflectionText(e.target.value)} placeholder="Take a moment to think about this..." className="w-full bg-white border border-gray-300 rounded-xl p-5 font-inter text-gray-700 text-lg min-h-[160px] focus:outline-none focus:ring-2 focus:ring-[#8528e2] focus:border-transparent resize-y shadow-inner" aria-label="Reflection input" data-id="element-1464" data-mp="p7th25h735u5" />
              <p className="font-inter text-gray-500 text-sm mt-4" data-id="element-1465" data-mp="bby2ucxqqix3">
                This is just for you — your response isn't stored or shared.
              </p>
            </div>
          </div>
        </section>

        {/* 7. Takeaway Block (Action Anchor) */}
        <section className="bg-[#1a0647] text-white py-20 px-4 md:px-8" data-id="element-1466" data-mp="2mvpm89rqwhzt">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1467" data-mp="2rv2ifl4ib1f2">
            <p className="font-inter text-2xl md:text-3xl font-bold leading-relaxed mb-12" data-id="element-1468" data-mp="2rxop7ijkpcw8">
              The single most important action you can take today is to save the
              phone numbers for your bank and credit card companies in your
              phone contacts.
            </p>

            <div className="flex flex-wrap justify-center gap-4 md:gap-6" data-id="element-1469" data-mp="243wtmggl8ysm">
              <button onClick={() => window.print()} className="flex flex-col items-center justify-center gap-3 bg-white/10 hover:bg-white/20 p-5 rounded-xl transition-colors min-h-[88px] min-w-[100px] border border-white/5" data-id="element-1470" data-mp="295vv1ksrplmx">
                <Printer size={28} className="text-[#01ced1]" aria-hidden="true" data-id="element-1471" data-mp="1lgbc31wm6odn" />
                <span className="font-manrope text-sm font-semibold" data-id="element-1472" data-mp="1qcickf68r7o7">
                  Print
                </span>
              </button>
              <a href="mailto:?subject=Helpful guide on digital crisis response&body=I found this guide on handling digital emergencies really helpful. You should check it out." className="flex flex-col items-center justify-center gap-3 bg-white/10 hover:bg-white/20 p-5 rounded-xl transition-colors min-h-[88px] min-w-[100px] border border-white/5 no-underline text-white" data-id="element-1473" data-mp="3hdakk1pa4hc">
                <Mail size={28} className="text-[#01ced1]" aria-hidden="true" data-id="element-1474" data-mp="3fyic0r3ynd09" />
                <span className="font-manrope text-sm font-semibold" data-id="element-1475" data-mp="3bwx47k6a9owx">
                  Email to myself
                </span>
              </a>
              <button onClick={() => navigator.clipboard.writeText(window.location.href)} className="flex flex-col items-center justify-center gap-3 bg-white/10 hover:bg-white/20 p-5 rounded-xl transition-colors min-h-[88px] min-w-[100px] border border-white/5" data-id="element-1476" data-mp="370gckfzvksf3">
                <LinkIcon size={28} className="text-[#01ced1]" aria-hidden="true" data-id="element-1477" data-mp="2scg3fxphyd9o" />
                <span className="font-manrope text-sm font-semibold" data-id="element-1478" data-mp="2ee8ae0vkzw3s">
                  Copy link
                </span>
              </button>
              <button title="Coming soon" className="flex flex-col items-center justify-center gap-3 bg-white/10 hover:bg-white/20 p-5 rounded-xl transition-colors min-h-[88px] min-w-[100px] border border-white/5 cursor-not-allowed opacity-70" data-id="element-1479" data-mp="362tbe02e1rz0">
                <QrCode size={28} className="text-[#01ced1]" aria-hidden="true" data-id="element-1480" data-mp="31ccto3rjbk6u" />
                <span className="font-manrope text-sm font-semibold" data-id="element-1481" data-mp="3p0w4or1yyb2m">
                  QR code
                </span>
              </button>
            </div>
          </div>
        </section>

        {/* 8. Completion Confirmation */}
        {isCompleted && <section className="bg-[#01ced1] py-16 md:py-24 px-4 md:px-8 animate-in fade-in slide-in-from-bottom-8 duration-1000" data-id="element-1482" data-mp="1dfltgwry24jv">
            <div className="max-w-3xl mx-auto text-center" data-id="element-1483" data-mp="1s36y4v4gwbtw">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg" data-id="element-1484" data-mp="1n43t3aohm5lx">
                <CheckCircle2 size={40} className="text-[#01ced1]" aria-hidden="true" data-id="element-1485" data-mp="zg43wf6tqn9g" />
              </div>
              <h2 className="font-manrope text-[#1a0647] text-3xl md:text-5xl font-bold mb-6 leading-tight" data-id="element-1486" data-mp="19dryn54fjurm">
                Nice work. The Crisis Response module is complete. You're better
                prepared to handle digital emergencies.
              </h2>

              <div className="bg-white/30 p-8 rounded-2xl mt-10 max-w-lg mx-auto backdrop-blur-sm border border-white/40 shadow-sm" data-id="element-1487" data-mp="2ku8idan0oxka">
                <p className="font-inter text-[#1a0647] text-xl font-bold mb-6" data-id="element-1488" data-mp="pcqpdvl0c765">
                  Share with your family
                </p>
                <a href="mailto:?subject=Helpful guide on digital crisis response&body=I found this guide on handling digital emergencies really helpful. You should check it out." className="bg-[#1a0647] text-white font-manrope font-bold text-lg px-8 py-4 rounded-xl hover:bg-[#2a0a6b] transition-colors min-h-[44px] inline-flex items-center justify-center gap-3 shadow-md w-full sm:w-auto no-underline" data-id="element-1489" data-mp="ubk1pkok5s4q">
                  <Share2 size={20} aria-hidden="true" data-id="element-1490" data-mp="1i0mf2gc5sytf" />
                  Send to a family member
                </a>
              </div>

              <div className="mt-16 text-left max-w-2xl mx-auto" data-id="element-1491" data-mp="1m1oht75abt05">
                <Link to="/resources" className="block bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all group no-underline border-2 border-transparent hover:border-[#8528e2]/50" data-id="element-1492" data-mp="1r2c97txfebuw">
                  <p className="font-manrope text-[#8528e2] font-bold text-sm uppercase tracking-wider mb-3" data-id="element-1493" data-mp="2627woxibksmo">
                    What's Next
                  </p>
                  <div className="flex items-center justify-between" data-id="element-1494" data-mp="1w769w8bio2n9">
                    <h3 className="font-manrope text-[#1a0647] text-2xl md:text-3xl font-bold" data-id="element-1495" data-mp="1s5b68x645d6b">
                      Back to Resources
                    </h3>
                    <div className="w-12 h-12 rounded-full bg-[#f8f8f8] flex items-center justify-center group-hover:bg-[#8528e2] group-hover:text-white transition-colors text-[#1a0647] shadow-sm" data-id="element-1496" data-mp="2ox6ioi951zve">
                      <ChevronRight size={24} aria-hidden="true" data-id="element-1497" data-mp="2f365ru7m7qc4" />
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </section>}
      </main>
    </div>;
}